// Base Imports
import React, { useEffect } from 'react'
import Link from 'next/link'
import type { GetStaticPropsContext } from 'next'
import dynamic from 'next/dynamic'

// Other Imports
import commerce from '@lib/api/commerce'
import { EmptyGuid, HOMEPAGE_SLUG } from '@components/utils/constants'
import { BLOG_PAGE_ID } from '@components/utils/constants'
import { BLOG_COLS } from '@components/utils/constants'
import withDataLayer, { PAGE_TYPES } from '@components/withDataLayer'
import { EVENTS_MAP } from '@components/services/analytics/constants'
import useAnalytics from '@components/services/analytics/useAnalytics'
import { MO_ENGAGE_EVENT } from 'hooks/moengage_event.modal'
import { IMG_PLACEHOLDER } from '@components/utils/textVariables'
import { useMoEngageEvent } from 'hooks/useMoengageEvent'

import NextHead from 'next/head'
import { useUI } from '@components/ui/context'
import { useRouter } from 'next/router'
import { GA_EVENT } from 'hooks/ga_event.modal'
import { LayoutWithTopStrip } from '@components/common/Layout'
import { recordGA4Event } from '@components/services/analytics/ga4'
import { IPagePropsProvider } from '@framework/contracts/page-props/IPagePropsProvider'
import { PagePropType, getPagePropType } from '@framework/page-props'
import useEventAnalytics from '@components/services/analytics/useEventAnalytics'
import { AnalyticsEventType } from '@components/services/analytics'

const Loader = dynamic(() => import('@components/ui/LoadingDots'))

declare const window: any

const PAGE_TYPE = PAGE_TYPES.Blog

function BlogList({ slugs, pageContents }: any) {
  let hostName = ''
  if (typeof window !== 'undefined') {
    hostName = window?.location?.hostname ?? ''
  }
  const { user, cartItems } = useUI()
  const { recordAnalytics } = useEventAnalytics()
  const trackMoEngageEvent = useMoEngageEvent()
  const { PageViewed } = EVENTS_MAP.EVENT_TYPES
  useAnalytics(PageViewed, {
    entity: JSON.stringify({
      id: slugs?.id,
      name: slugs?.name,
      metaTitle: slugs?.metaTitle,
      MetaKeywords: slugs?.metaKeywords,
      MetaDescription: slugs?.metaDescription,
      Slug: slugs?.slug,
      Title: slugs?.title,
      ViewType: slugs?.viewType,
    }),
    entityName: PAGE_TYPE,
    pageTitle: slugs?.title,
    entityType: 'Page',
    entityId: slugs?.id,
    eventType: 'PageViewed',
  })
  useEffect(()=>{
    recordAnalytics(AnalyticsEventType.OTHER_PAGE, { type: 'other', data: { cartItems } })
  },[])

  function blogClick(blog: any, blogId: any, areaClick: any) {
    recordGA4Event(window, GA_EVENT.BLOG_CLICK, {
      title: blog?.title,
      content_click: areaClick,
      article_date: blog?.updated,
      position: blogId + 1,
      loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
    })

    trackMoEngageEvent(
      MO_ENGAGE_EVENT.BLOG_CLICK,
      {
        Title: blog?.title,
        Content_Click: areaClick,
        Article_Date: blog?.updated,
        Position: blogId + 1,
      },
      window
    )
  }

  const router = useRouter()
  return !pageContents ? (
    <>
      <div className="flex w-full text-center flex-con">
        <Loader />
      </div>
    </>
  ) : (
    <>
      {hostName && <input className="inst" type="hidden" value={hostName} />}
      <NextHead>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
        <link
          rel="canonical"
          id="canonical"
          href={'https://www.damensch.com' + router.asPath}
        />
        <title>DaMENSCH Blog - Men Trending Clothes</title>
        <meta name="title" content="DaMENSCH Blog - Men Trending Clothes" />

        <meta
          name="description"
          content="Welcome to the DaMENSCH Blog, an ultimate guide to men's style and fashion wear. Step into a world where sophistication meets innovation, and explore a lot of insights that will elevate your fashion need."
        />
        <meta name="keywords" content="Blogs, DaMENSCH Blogs" />
        <meta
          property="og:image"
          content={
            pageContents?.pages?.[1]?.fields?.blogheader?.[0]
              ?.blogheader_mainimage
          }
        />
        <meta
          property="og:title"
          content="DaMENSCH Blog - Men Trending Clothes"
          key="ogtitle"
        />
        <meta
          property="og:description"
          content="Welcome to the DaMENSCH Blog, an ultimate guide to men's style and fashion wear. Step into a world where sophistication meets innovation, and explore a lot of insights that will elevate your fashion need."
          key="ogdesc"
        />
      </NextHead>
      <div className="w-full pt-4">
        <div className="container px-4 mx-auto">
          <div className="flex flex-col py-2 sm:py-10">
            <div className="flex flex-col justify-center px-0 sm:px-6 lg:px-0">
              <h1 className="text-xl font-bold tracking-tight text-left text-gray-900 heading-h2 sm:text-3xl">
                <span className="bg-white">Blog list</span>
              </h1>
            </div>
          </div>

          <div className="grid max-w-lg gap-5 mx-auto mt-4 lg:grid-cols-3 lg:max-w-none">
            {pageContents?.pages?.map((blog: any, blogId: number) => (
              <div
                className="flex flex-col overflow-hidden border rounded-lg hover:shadow-lg"
                key={blogId}
              >
                {blog?.fields?.blogheader?.map(
                  (blogImg: any, imgid: number) => (
                    <div className="flex-shrink-0" key={imgid}>
                      <Link legacyBehavior href={`/${blog?.slug}`} passHref>
                        <a onClick={() => blogClick(blog, blogId, 'Image')}>
                          <img
                            placeholder="blur"
                            className="object-cover w-full h-auto-mob sm:h-64"
                            src={
                              blogImg.blogheader_mainimage || IMG_PLACEHOLDER
                            }
                            alt={blog?.title}
                          />
                        </a>
                      </Link>
                    </div>
                  )
                )}
                <div
                  className="flex flex-col justify-between flex-1 p-6 bg-white"
                  key={blogId}
                >
                  <div className="flex-1 mb-8">
                    <Link legacyBehavior href={`/${blog?.slug}`} passHref>
                      <a
                        className="block mt-2"
                        onClick={() => blogClick(blog, blogId, 'Title')}
                      >
                        <p className="text-xl font-bold text-gray-900">
                          {blog.title}
                        </p>
                      </a>
                    </Link>
                  </div>
                  <div className="flex justify-between">
                    <p className="text-sm text-black">
                      Published on{' '}
                      <span className="font-semibold">{blog?.updated}</span>
                    </p>
                    <Link legacyBehavior href={`/${blog?.slug}`} passHref>
                      <a
                        onClick={() => blogClick(blog, blogId, 'Read More')}
                        className="text-sm text-orange"
                      >
                        Read more
                      </a>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

BlogList.Layout = LayoutWithTopStrip

export default withDataLayer(BlogList, PAGE_TYPE, true, LayoutWithTopStrip)

export async function getStaticProps({
  preview,
  locale,
  locales,
}: GetStaticPropsContext) {
  const config = { locale, locales }
  const slugsPromise = commerce.getSlugs({ slug: HOMEPAGE_SLUG })
  const slugs = await slugsPromise
  const infraPromise = commerce.getInfra()
  const infra = await infraPromise

  const pagesPromise = commerce.getAllPages({ config, preview })
  const siteInfoPromise = commerce.getSiteInfo({ config, preview })
  const { pages } = await pagesPromise
  const { categories, brands } = await siteInfoPromise

  // // ---- Display data from content entries ----
  // const id = "05905b59-84a7-41a3-b992-5137c11f86f7";
  // const PageContentsPromise = commerce.getPageContent({ id: id, slug: slugs?.slug });
  // const pageContents = await PageContentsPromise;

  // ---- Display data from pages ----
  //const pageId = "7c79d1b2-53e1-4435-a64f-8298446f3546";
  const BlogContentsPromise = commerce.getBlogList({
    pagetypeId: BLOG_PAGE_ID, //Constant pageId,
    skip: 0, //skip,
    pagesize: 100, //pagesize,s
    sortby: 3, //sortby,
    sortorder: 1, //sortorder,
    cols: BLOG_COLS, //"blogheader.blogheader_mainimage",
  })
  const blogContents = await BlogContentsPromise
  const props: IPagePropsProvider = getPagePropType({ type: PagePropType.COMMON }) as IPagePropsProvider
  const commonPageProps = await props.getPageProps({ cookies: {} })
  return {
    props: {
      ...commonPageProps,
      categories,
      brands,
      pages,
      slugs,
      globalSnippets: infra?.snippets ?? [],
      snippets: slugs?.snippets ?? [],
      pageContents: blogContents ?? {},
    },
    revalidate: 60,
  }
}
